import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { devLog } from "@/lib/log";

const VendorReviewsManager = ({ slug }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", rating: 5, comment: "" });
  const [adding, setAdding] = useState(false);

  const reload = async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/reviews`);
      setRows((r.data || []).filter((x) => x.vendor_slug === slug));
    } catch (e) {
      devLog("ld_admin_reviews load failed:", e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // Reload only when the vendor slug changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { status });
      setRows((rs) =>
        rs.map((r) => (r.id === id ? { ...r, status } : r))
      );
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not update review", {
        duration: 2400,
      });
    }
  };
  const deleteRow = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
    } catch (e) {
      devLog("ld_admin_reviews delete failed:", e?.message);
      toast("Could not delete review", { duration: 2400 });
    }
  };
  const addReview = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.comment.trim()) {
      toast("Add a name and comment", { duration: 2400 });
      return;
    }
    setAdding(true);
    try {
      const r = await api.post(`/admin/reviews`, {
        vendor_slug: slug,
        reviewer_name: form.name.trim(),
        rating: Number(form.rating),
        comment: form.comment.trim(),
        status: "approved",
      });
      setRows((rs) => [r.data, ...rs]);
      setForm({ name: "", rating: 5, comment: "" });
      toast("Review added", { duration: 1800 });
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not add review", {
        duration: 2400,
      });
    } finally {
      setAdding(false);
    }
  };

  if (!slug) {
    return (
      <p className="mt-2 text-[12px] text-[var(--ld-ink-muted)] italic">
        Approve this vendor to start collecting reviews.
      </p>
    );
  }

  return (
    <div data-testid="vendor-reviews-manager" className="mt-4 space-y-4">
      <div className="border border-[var(--ld-border)]/80">
        {loading && (
          <div className="px-4 py-4 text-[12px] text-[var(--ld-ink-muted)] text-center">
            Loading reviews…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="px-4 py-4 text-[12px] text-[var(--ld-ink-muted)] italic text-center">
            No reviews yet.
          </div>
        )}
        {rows.map((r) => (
          <div
            key={r.id}
            data-testid={`vendor-review-row-${r.id}`}
            className="px-4 py-3 border-b border-[var(--ld-border)]/70 last:border-b-0"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[13px] text-[var(--ld-ink)]">
                <span className="font-medium">{r.reviewer_name}</span>
                <span className="text-[var(--ld-ink-muted)] ml-2">
                  {"★".repeat(r.rating)}
                  {"☆".repeat(5 - r.rating)}
                </span>
              </p>
              <span
                className={
                  "inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border " +
                  (r.status === "approved"
                    ? "border-emerald-700/40 text-emerald-700"
                    : r.status === "rejected"
                    ? "border-red-700/40 text-red-700"
                    : "border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)]")
                }
              >
                {r.status}
              </span>
            </div>
            <p className="mt-1 text-[12.5px] text-[var(--ld-ink)]/85 whitespace-pre-wrap">
              {r.comment}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <select
                data-testid={`vendor-review-status-${r.id}`}
                value={r.status}
                onChange={(e) => updateStatus(r.id, e.target.value)}
                className="bg-transparent border border-[var(--ld-ink)]/25 text-[10px] tracking-[0.12em] uppercase px-2 py-1 focus:outline-none focus:border-[var(--ld-gold-deep)]"
              >
                <option value="pending">pending</option>
                <option value="approved">approved</option>
                <option value="rejected">rejected</option>
              </select>
              <button
                onClick={() => deleteRow(r.id)}
                data-testid={`vendor-review-delete-${r.id}`}
                className="text-[10px] tracking-[0.18em] uppercase text-red-700 border border-red-700/30 px-2 py-1 hover:bg-red-700/5"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <form
        onSubmit={addReview}
        data-testid="vendor-review-add-form"
        className="grid grid-cols-12 gap-2"
      >
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
          placeholder="Reviewer name"
          className="col-span-7 h-9 bg-transparent border border-[var(--ld-ink)]/25 px-2 text-[12px] text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)]"
        />
        <select
          value={form.rating}
          onChange={(e) => setForm((s) => ({ ...s, rating: e.target.value }))}
          className="col-span-2 h-9 bg-transparent border border-[var(--ld-ink)]/25 px-2 text-[12px]"
        >
          {[5, 4, 3, 2, 1].map((n) => (
            <option key={n} value={n}>
              {n}★
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={adding}
          className="col-span-3 h-9 text-[10px] tracking-[0.18em] uppercase bg-[var(--ld-ink)] text-[var(--ld-ivory)] hover:bg-[var(--ld-gold-deep)] transition-colors disabled:opacity-60"
        >
          {adding ? "Adding…" : "Add review"}
        </button>
        <textarea
          rows={2}
          value={form.comment}
          onChange={(e) => setForm((s) => ({ ...s, comment: e.target.value }))}
          placeholder="Comment"
          className="col-span-12 bg-transparent border border-[var(--ld-ink)]/25 px-2 py-1.5 text-[12px] text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)]"
        />
      </form>
    </div>
  );
};

export default VendorReviewsManager;
